/**
 * TriggerButton — the compact icon-only button every state of the opt-in "Edit using Expression
 * Builder" trigger renders, and the styling that goes with it.
 *
 * ── Why this is its own module ─────────────────────────────────────────────────────────────────
 *
 * It used to live inside `ExpressionBuilderTrigger.tsx`, which is behind `React.lazy`. That was fine
 * while every state of the button was reached *after* the chunk loaded — but the one state that
 * cannot be is the state where the chunk **fails** to load. `ExpressionBuilderErrorBoundary` has to
 * render the unavailable button without the module that failed, so the button has to be reachable
 * without it.
 *
 * The constraint that makes this safe: **nothing in this file may import
 * `@bizfirst/expressions-designer`, directly or transitively.** It is imported eagerly by
 * `FormField.tsx` (through the error boundary), so anything it pulls in is paid for by every form in
 * the product. `CodeIcon` comes from `@atlas-forms/ui-components-react`, which `FormField.tsx`
 * already imports eagerly for its own icons, so this adds nothing to that bundle.
 */

import React from 'react';
import { CodeIcon } from '@atlas-forms/ui-components-react';

/** The one accessible name every state of the trigger uses, enabled or not. */
export const BUTTON_LABEL = 'Edit using Expression Builder';

/**
 * `data-state` on the rendered button, so a test — or a human in devtools — can tell *why* a trigger
 * is disabled rather than only that it is.
 *
 *   - `ready`        — configured, loaded, clickable.
 *   - `unconfigured` — no host has called `configureExpressionBuilder()`.
 *   - `loading`      — the Expressions Designer chunk is in flight.
 *   - `disabled`     — the field itself is disabled/readonly, so the chunk is never requested.
 *   - `unavailable`  — a lazy chunk rejected; the trigger is gone but the field is not.
 */
export type TriggerState = 'ready' | 'unconfigured' | 'loading' | 'disabled' | 'unavailable';

export interface TriggerButtonProps {
  onClick?: () => void;
  disabled: boolean;
  style: React.CSSProperties;
  title: string;
  controlID: string;
  /**
   * The transient loading placeholder gets its **own** id rather than reusing the trigger's.
   * Sharing one meant a `findByTestId` could resolve the placeholder and hand back a node that
   * `Suspense` detached a tick later — a genuine "element could not be found in the document"
   * failure, and exactly the kind of state confusion a caller would hit at runtime too if it tried
   * to address the button while the chunk was still in flight.
   */
  testID?: string;
  state: TriggerState;
}

/**
 * The compact, icon-only button Plan/05 §3 asks for ("small icon-only trigger, not the full-width
 * default button — this sits beside a native input"). Supplied to the launcher through
 * `renderTrigger`, which the launcher renders verbatim with no wrapper of its own, so it drops
 * straight into `FormField`'s flex row.
 */
export function TriggerButton({
  onClick,
  disabled,
  style,
  title,
  controlID,
  testID = 'af-expression-builder-trigger',
  state,
}: TriggerButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      // Never a submit button — `type="button"` is what keeps clicking it from submitting the form.
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={BUTTON_LABEL}
      data-testid={testID}
      data-control-id={controlID}
      data-state={state}
      className="af-expr-trigger"
      style={style}
    >
      <CodeIcon width={16} height={16} />
    </button>
  );
}

/**
 * Plain function rather than a hook, because `ExpressionBuilderErrorBoundary` is a class component
 * and cannot call one. `ExpressionBuilderTrigger` wraps it in `useMemo` at its own call sites, which
 * is where the memoisation actually mattered.
 */
export function getTriggerStyle(
  theme: 'light' | 'dark',
  disabled: boolean,
): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: '38px',
    height: '38px',
    padding: 0,
    borderRadius: '6px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
    background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : '#fff',
    color: disabled
      ? theme === 'dark'
        ? 'rgba(148,163,184,0.5)'
        : '#9ca3af'
      : theme === 'dark'
        ? '#e5e7eb'
        : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.2s, background 0.2s',
  };
}
